import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DesignationsPage = () => {
  const [designations, setDesignations] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadDesignations();
  }, []);

  const loadDesignations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/designations`, { withCredentials: true });
      setDesignations(res.data);
    } catch (error) {
      console.error('Error loading designations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/designations`, { name }, { withCredentials: true });
      setOpen(false);
      setName('');
      loadDesignations();
    } catch (error) {
      console.error('Error creating designation:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/api/designations/${id}`, { withCredentials: true });
      loadDesignations();
    } catch (error) {
      console.error('Error deleting designation:', error);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? designations.filter((d) => (d.name || '').toLowerCase().includes(q)) : designations;
  }, [designations, search]);
  const { paged, controls, startIdx } = usePagination(filtered, { storageKey: 'designations-page-size', testIdPrefix: 'designation' });

  return (
    <div data-testid="designations-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Designations
          </h1>
          <p className="text-[#52525B] text-sm">Manage job designations</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-designation-btn"
              className="bg-[#1D9E75] hover:bg-[#0F6E56] text-white rounded-sm"
            >
              <Plus size={20} className="mr-2" />
              Add Designation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Designation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Designation Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="e.g., Software Engineer, Manager"
                  data-testid="designation-name-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1D9E75] hover:bg-[#0F6E56]" data-testid="save-designation-btn">
                  Save
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-[#E4E4E7]">
        <div className="p-3 border-b">
          <Input
            placeholder="Search designation name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
            data-testid="designation-search"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F5]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">#</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Designation Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-[#52525B]">
                  {designations.length === 0 ? 'No designations found. Add your first designation.' : 'No matches.'}
                </TableCell>
              </TableRow>
            ) : paged.map((desig, idx) => (
              <TableRow key={desig.id} data-testid={`designation-row-${desig.id}`}>
                <TableCell className="text-gray-400">{startIdx + idx + 1}</TableCell>
                <TableCell className="font-medium">{desig.name}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[#DC2626] hover:text-[#DC2626]"
                    onClick={() => handleDelete(desig.id)}
                    data-testid={`delete-designation-${desig.id}`}
                  >
                    <Trash size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {controls}
      </Card>
    </div>
  );
};
